/*#
# popup-surface

**A popup is another SURFACE, not more rows in the one you have.**

```js
import { b3d } from 'tosijs-3d'
import { demoStage, orbitCam } from 'tosijs-3d/demo-utils'
import { svgElements, elements } from 'tosijs'
const { svg, rect, text } = svgElements

// A little SVG panel, used for both the opener and its popups.
// No `rx` on the background: the MESH is rounded, so rounding the svg too
// would only add a darker fringe inside an already-rounded silhouette.
const card = (title, lines, w = 220, h = 130) =>
  svg(
    { viewBox: `0 0 ${w} ${h}`, width: w, height: h },
    rect({ x: 0, y: 0, width: w, height: h, fill: '#1b2430' }),
    rect({ x: 0, y: 0, width: w, height: 26, fill: '#2c3b4e' }),
    text({ x: 12, y: 18, fill: '#e6edf5', 'font-size': 13, 'font-family': 'sans-serif' }, title),
    ...lines.map((l, i) =>
      text({ x: 12, y: 48 + i * 18, fill: '#9fb0c3', 'font-size': 12, 'font-family': 'sans-serif' }, l)
    )
  )

let opened = []
const scene = b3d(
  {
    sceneCreated(el) {
      orbitCam(el, { radius: 4.2, beta: Math.PI / 2.6, target: [0, 1.2, 0] })

      // The opener: one ordinary in-scene panel.
      const base = el.make.plane({ width: 1.6, height: 0.95, y: 1.2, color: '#101820' })
      base.isPickable = true

      const spawn = (n) => {
        const pop = el.openPopup({
          svg: card(`popup ${n}`, ['spawned as its own surface', 'tear off, then drag me']),
          opener: base,
          width: 0.9,
          // Fan them out and step each one nearer the viewer, so depth does the
          // stacking that a z-index would do flat.
          offset: { x: 0.55 + n * 0.16, y: 0.3 - n * 0.22, z: -0.06 * (n + 1) },
        })
        opened.push(pop)
        return pop
      }

      // popup 0 stays OWNED — parented to the panel, so it travels with it.
      // Drag it and it moves ON the panel: it still travels with it.
      spawn(0)
      spawn(1).tearOff()
      // A MODAL blocks pointer access to everything behind it (the other
      // popups included) until it closes. The camera still moves.
      const m = el.openPopup({
        svg: card('modal', ['blocks the ones behind it', 'camera still moves']),
        width: 0.9,
        offset: { x: -0.8, y: 0.2, z: -0.3 },
        modal: true,
      })
      opened.push(m)
    },
  },
  ...demoStage({ size: 12, tiles: 8, texture: '/tosi-warhol-testgrid.svg', timeOfDay: 11 }),
)

preview.append(scene)
```

A menu cascade, a dropdown's open list, a debug readout — in a browser these are
absolutely positioned inside the one window you were given, and overlapping them
is a z-index problem. In a scene none of that scarcity is real: planes are cheap,
depth is an actual axis, and a panel can simply be *somewhere else*.

```javascript
import { openPopup } from 'tosijs-3d'

const pop = openPopup(el, { svg: mySvg, opener: panel.mesh, width: 1.1 })
pop.tearOff() // promote it: stays where you put it, and can be dragged
pop.close()
```

## Why not lay it out inside the opener

You cannot draw outside the SVG that becomes the texture, so a popup that lives
*in* that SVG is cropped by it, or has to grow the panel's layout. A popup does
not have to live there. It can be **another texture on another plane**, and then
the constraint does not bind.

What that buys, beyond the dropdown:

- **The opener does not reflow.** Opening a debug tool leaves the author's
  controls where they were, instead of moving what you were reading because you
  asked about something else.
- **Tear-offs are not a separate feature.** A popup that is already its own surface
  becomes "leave this open over there" by re-parenting, not by new machinery.
- **Depth replaces z-index.** `lift` pulls the popup toward the viewer, which is
  the effect flat UI fakes with shadows and scrims.

## Chrome: a title bar you can grab, and an x

A popup draws a **move** glyph and a **close** glyph into the top of its own
SVG, and the top `gripHeight` of the panel is the only place a drag starts.

That grip is not decoration. A popup with controls in it needs its own pointer
events — if a drag began anywhere on the surface, every button press inside it
would be a failed drag instead. Restricting the gesture to a title bar is what
lets the rest of the panel be interactive, and the icon is what makes that rule
visible rather than something you have to be told.

The glyphs are injected here rather than left to the caller, and their
rectangles and the hit regions come from ONE layout in viewBox units, so the
visible × is always where pressing closes. Two derivations of the same rectangle
drift: on some aspect ratios the × would sit outside its own target, and a press
on it would fall through to the drag and tear the popup off instead of closing
it. The close TARGET is sized separately from the glyph, at least as wide as the
bar is tall, so a small × is still easy to hit with a controller ray. They are drawn with
`iconGlyph`, not `svgIcons`, because this SVG becomes a TEXTURE and
`currentColor` resolves against nothing there.

## Ownership, and what tearing off changes

A popup is **owned**: parented to its opener, so it travels with it and is
disposed with it. `tearOff()` promotes it to world space **preserving its world
pose** (Babylon's `setParent(null)` does that arithmetic for us), after which it
is nobody's child, stays where it is, and — if `draggable` — can be grabbed and
moved. Ownership is a lifetime statement, not a position one, the same split as
riding an elevator.

## Why parenting to the opener is fine here

The DOM habit is to portal a popup OUT to a positioned ancestor, and it is worth
being clear that the reason does not survive the trip. A DOM parent **crops**
(`overflow`) and traps you in its **stacking context**; you escape it to avoid
being clipped and to control what sits in front. A Babylon parent does neither —
it is a transform and nothing else — so there is nothing to escape, and
ownership costs nothing.

The cropping constraint is real, though; it just lives one level down. **The
TEXTURE is the crop.** An `openPanel` overlay is clipped by the surface's viewBox
exactly the way a child is clipped by `overflow: hidden`, and no amount of
re-parenting changes that, because it was never a scene-graph problem. That is
why the fix is a second PLANE rather than a different parent.

Inheriting the opener's scale and rotation is then a feature rather than a leak:
a panel scaled down onto a wrist should take its popups with it, at its size and
its angle. `tearOff()` remains the way to opt out, and it is a deliberate act.

On a plane, a panel's popups get a surface of their own in front of it, so
the panel's SVG never crops them; both `panelScene` and the in-scene settings
panel route their popups that way.

## Budget

Every surface is a texture. Two or three floating panels are nothing; twenty is
a VRAM problem on a Quest, and VR is the tier with the least headroom. Callers
should treat concurrent surfaces as a budget — the ambient allocator's rule
applies, so something that cannot have its honest minimum should switch OFF
rather than degrade.

*/
/*{ "parent": "ui-containers", "order": 30 }*/

import * as BABYLON from '@babylonjs/core'
import { b3dSvgPlane, type B3dSvgPlane } from './b3d-svg-plane.js'
import { claimPointer, panelLayerOf } from './frame-panel.js'
import { iconGlyph } from './svg-icons.js'
import { chromeLayout, chromeHit, uvToViewBox } from './popup-chrome.js'
import { panelRim } from './panel-rim.js'

/** Tag a node as belonging to the TEXTURE presentation only. */
function markPresentation<T extends Element>(el: T): T {
  el.setAttribute('data-presentation', 'texture')
  return el
}
import { cameraIsAttached } from './b3d-utils.js'
import type { B3d } from './tosi-b3d.js'

export interface PopupSurfaceOptions {
  /** The panel's content. Its viewBox aspect sets the plane's height. */
  svg: SVGSVGElement
  /**
   * What this popup belongs to. Parented to it, so it travels with it and dies
   * with it. Omit for a popup that is born free.
   */
  opener?: BABYLON.TransformNode | BABYLON.AbstractMesh | null
  /** World width; height follows the svg's aspect. Default 1. */
  width?: number
  /** Texture resolution. Default 512 — each surface is a texture, see Budget. */
  resolution?: number
  /** Position relative to the opener (or world, if there is none). */
  offset?: { x?: number; y?: number; z?: number }
  /**
   * Extra nudge toward the viewer, in world units. Depth is what makes a popup
   * read as "on top" without a z-index. Default 0.02 — enough to beat
   * co-planar z-fighting, small enough not to look detached.
   */
  lift?: number
  /**
   * Can it be grabbed and moved? Default true.
   *
   * Dragging an OWNED popup moves it relative to its opener; it stays owned
   * and keeps travelling with it. `tearOff()` is how it is let go.
   */
  draggable?: boolean
  /**
   * Told whenever this popup closes — including from its OWN × glyph.
   *
   * A popup shown in two presentations is ONE popup wearing two faces, and only
   * its opener knows about the other face. Without this, pressing × on the
   * in-scene one closed that plane and left the flat one up, and the opener
   * still believed it had a popup open. Tonio, from a headset: "closing the
   * keyboard using the close button doesn't close it in both contexts… it's
   * just closing that particular panel, which is kind of weird."
   *
   * Fires ONCE — `close()` is idempotent.
   */
  handleClosed?: () => void
  /**
   * Fraction of the panel's height that acts as the TITLE BAR — the only place
   * a drag starts. Default 0.2; `0` makes the whole panel draggable.
   *
   * A grip rather than the whole surface, because a popup with controls in it
   * needs its own pointer events: if dragging started anywhere, every button
   * press would try to move the panel. Tonio: "indicate you can only drag them
   * by the title bar in case the popup needs pointer stuff".
   */
  gripHeight?: number
  /** Draw the move / close glyphs into the title bar. Default true — an
   * affordance nobody can see is not an affordance. */
  chrome?: boolean
  /**
   * A modal blocks pointer interaction with everything BEHIND it until it
   * closes. The camera still moves — a modal owns the UI, not your head, and in
   * a headset you cannot take someone's view away without making them ill.
   */
  modal?: boolean
}

export interface PopupSurface {
  /** The plane element (an `AbstractMesh`, so it has x/y/z and rx/ry/rz). */
  plane: B3dSvgPlane
  /** Dispose it. Safe to call twice. */
  close(): void
  /**
   * Promote to world space, preserving world pose: it stops being owned, stays
   * where it is, and becomes draggable. Idempotent.
   */
  tearOff(): void
  readonly tornOff: boolean
  /** Internal: the depth offset currently applied by the stack, so restacking
   * replaces it rather than accumulating. */
  stackLift?: { x: number; y: number; z: number }
  /** Internal: does this one block what is behind it? */
  modal?: boolean
  /** Internal: the panel's viewBox and the chrome geometry derived from it —
   * shared by the drawing and the hit testing so they cannot disagree. */
  vbWidth: number
  vbHeight: number
  chromeLayout: ReturnType<typeof chromeLayout>
  /** Internal: start the move gesture (called after the grip hit test). */
  /**
   * Internal: start a drag, grabbing at `pickedPoint` so the panel keeps the
   * offset you grabbed it by instead of jumping to centre on the pointer.
   */
  beginDrag(
    pointerId: number,
    pickedPoint?: BABYLON.Vector3,
    ray?: BABYLON.Ray
  ): void
  /** Internal: end it on pointerup — the behaviour's own release is disabled. */
  endDrag(): void
  /** Bring this popup to the front of the stack. */
  toFront(): void
}

/**
 * Open a popup as its own surface.
 *
 * Returns immediately; the plane mounts through the normal `B3dChild` pull
 * lifecycle, so the parenting and placement are applied once its mesh exists
 * rather than being guessed at a fixed delay.
 */
/*
Open popups per scene, in depth order — front of the list is nearest the viewer.

A WeakMap so a disposed scene takes its popups with it: this is a UI registry,
not a reason to keep a scene alive.
*/
const openPopups = new WeakMap<B3d, PopupSurface[]>()

/** Depth step between stacked popups, world units. Big enough to beat
 * z-fighting at panel scale, small enough that a stack still reads as a stack. */
const DEPTH_STEP = 0.012

/**
 * Restack so `front` is nearest the viewer.
 *
 * Depth does the job a z-index does flat — but unlike a z-index it is a real
 * position, so it has to be re-applied rather than merely re-sorted.
 */
function bringToFront(owner: B3d, front: PopupSurface): void {
  const list = openPopups.get(owner)
  if (list == null) return
  const i = list.indexOf(front)
  if (i > 0) {
    list.splice(i, 1)
    list.unshift(front)
  }
  const cam = owner.scene?.activeCamera
  list.forEach((p, depth) => {
    const mesh = p.plane.mesh
    if (mesh == null) return
    // Toward the CAMERA, not along world -Z: "in front" is a viewer-relative
    // claim, and a stack arranged along a world axis is only correct from one
    // side of the room.
    const toward =
      cam != null
        ? cam.globalPosition.subtract(mesh.absolutePosition).normalize()
        : new BABYLON.Vector3(0, 0, -1)
    const lift = toward.scale(DEPTH_STEP * (list.length - depth))
    /*
    INTO THE PARENT'S SPACE. The element's x/y/z are local to the panel the
    popup hangs from, and that panel is tilted and scaled in a headset, so a
    world vector written into them pointed somewhere else: the stack was
    spread sideways and its members stayed in one plane, flickering where
    they overlapped.
    */
    const parent = mesh.parent as BABYLON.TransformNode | null
    if (parent != null) {
      const inv = parent.getWorldMatrix().clone().invert()
      BABYLON.Vector3.TransformNormalToRef(lift, inv, lift)
    }
    // From the ELEMENT's position, not the mesh's: the mesh only catches up
    // at the next render, so a second restack in the same frame read a
    // position without the first one's offset, subtracted that offset anyway,
    // and left the popup a step short, level with its neighbour.
    p.plane.x = Number(p.plane.x) + lift.x - (p.stackLift?.x ?? 0)
    p.plane.y = Number(p.plane.y) + lift.y - (p.stackLift?.y ?? 0)
    p.plane.z = Number(p.plane.z) + lift.z - (p.stackLift?.z ?? 0)
    p.stackLift = { x: lift.x, y: lift.y, z: lift.z }
  })
}

/** One pointer hook per scene, installed lazily — not one per popup. */
const wired = new WeakSet<B3d>()

/**
 * Make everything behind the topmost modal un-pickable, and restore it when the
 * modal goes away.
 *
 * ⚠️ The obvious implementation is to cancel the pointer event
 * (`skipOnPointerObservable = true`) when the press is not on the modal. I did
 * that first and it BROKE MOUSE CONTROL: Babylon's camera pointer inputs route
 * through `onPointerObservable` too, so cancelling there kills orbit along with
 * the UI. Tonio, immediately: "I can only rotate the view in some places. Other
 * places mouse doesn't work at all."
 *
 * So the block is expressed as what it actually means — those panels are not
 * targets right now — instead of as a veto over the whole pointer pipeline. The
 * camera is untouched, which matters more in a headset than anywhere: a modal
 * owns the UI, not your head, and freezing someone's view is how you make them
 * ill.
 */
/**
 * WHO IS PICKABLE while a modal is open — the pure decision, extracted so the
 * rule can be tested without a scene.
 *
 * `true` for everything when no modal is open (which is what makes `close()`
 * restore the stack — regress that and every popup becomes permanently
 * untargetable while the camera still works, which reads as "the UI died").
 * With a modal open, only the FIRST modal in the list is pickable.
 */
export function modalPickable(popups: { modal?: boolean }[]): boolean[] {
  const modal = popups.find((p) => p.modal === true) ?? null
  return popups.map((p) => modal == null || p === modal)
}

function applyModalBlocking(owner: B3d): void {
  const list = openPopups.get(owner)
  if (list == null) return
  const pickable = modalPickable(list)
  // A modal is a menu: the glass gamepad would sit over it and take its taps.
  owner._suppressGamepad?.(
    'modal-popup',
    list.some((p) => p.modal === true)
  )
  list.forEach((p, i) => {
    const mesh = p.plane.mesh
    if (mesh == null) return
    mesh.isPickable = pickable[i]
  })
}

/**
 * Wire click-to-front and modal blocking for a scene.
 *
 * `onPointerObservable`, NOT `onPrePointerObservable`.
 *
 * Both matter and the ordering is the whole bug. `PointerDragBehavior` also
 * listens on POINTERDOWN, and its handler releases any drag whose
 * `_activeDragButton` doesn't match the event's button. `startDrag()` leaves
 * that at -1, so starting the drag from the PRE observable meant Babylon's own
 * pass ran afterwards and killed it inside the same event: one dragStart, one
 * dragEnd, zero drags, and a popup that jumps to front but never follows the
 * pointer. Starting AFTER that pass leaves the drag alive.
 *
 * Modal blocking does not need the pre-pass, because it is done by making the
 * panels behind un-pickable rather than by vetoing events.
 */
function wirePointer(owner: B3d): void {
  if (wired.has(owner)) return
  wired.add(owner)
  const scene = owner.scene
  if (scene == null) return

  /*
  THE CAMERA HAS TO YIELD, or the drag is invisible.

  Starting our drag was necessary and not sufficient: the ArcRotateCamera's
  pointer input is on this same observable and registers FIRST (the camera
  exists before any popup), so a press on a panel began a camera orbit as well.
  Both ran; the orbit is what you saw. Tonio, driving it: "I can drag the window
  around. I cannot move the pop-ups, none of them. They just cause me to drag
  the windows."

  `panelScene` already solved this for in-scene UI — claim the gesture, detach
  the camera, re-attach on release. Same move here. `cameraIsAttached` is the
  guard from the pause bug: only re-attach a camera that WAS attached, or a
  scene whose camera is deliberately unattached gets handed control it never
  had.
  */
  let yielded: BABYLON.Camera | null = null
  const yieldCamera = (): void => {
    const cam = scene.activeCamera
    if (cam == null || !cameraIsAttached(cam)) return
    cam.detachControl()
    yielded = cam
  }
  const restoreCamera = (): void => {
    if (yielded == null) return
    yielded.attachControl(
      scene.getEngine().getRenderingCanvas() as HTMLCanvasElement,
      true
    )
    yielded = null
  }

  /*
  AND A BACKSTOP ON `window`, because the scene never hears every release.

  A pointer that goes up OUTSIDE the canvas — off the edge mid-drag, over
  another element, or on a window that loses focus — produces no scene pointer
  event, so the observable above never runs and the camera stays detached. That
  is the same dead scene by a different route, and it is the LIKELIER one in
  practice: dragging a panel toward the edge is exactly how you leave the canvas.

  `pointercancel` too — the OS steals the pointer for gestures and scrolls, and
  a cancelled drag is still a drag that took the camera.

  This is deliberately a blunt "if we hold it, give it back" rather than an
  attempt to decide whether the release was ours. Restoring a camera nobody
  yielded is a no-op (`yielded` is null); failing to restore one we did costs the
  user the scene.
  */
  const releaseAnywhere = (): void => {
    if (yielded != null) restoreCamera()
  }
  window.addEventListener('pointerup', releaseAnywhere)
  window.addEventListener('pointercancel', releaseAnywhere)
  scene.onDisposeObservable.add(() => {
    window.removeEventListener('pointerup', releaseAnywhere)
    window.removeEventListener('pointercancel', releaseAnywhere)
    wired.delete(owner)
  })

  /*
  ON THE LAYER SCENES TOO. A utility layer that catches a press marks it
  skipped for the main scene's observers, so a popup in a layer would never
  be heard here. The same handler is added to each layer scene a popup is
  made in (`listenIn`), once; `seen` stops one event being handled twice
  where both scenes do get it.
  */
  let seen: Event | null = null
  const onPointer = (
    info: BABYLON.PointerInfo,
    state?: BABYLON.EventState
  ): void => {
    const ev = info.event as unknown as Event
    if (
      ev != null &&
      ev === seen &&
      info.type !== BABYLON.PointerEventTypes.POINTERUP
    )
      return
    seen = ev
    const list = openPopups.get(owner)

    /*
    RELEASE FIRST, AND UNCONDITIONALLY — before any "is there a popup?" test.

    Restoring the camera used to sit BEHIND the empty-list guard, so the release
    depended on a popup still being open at the moment the button came up. It
    usually is, which is why this survived: drag a panel, let go, the list is
    non-empty, the camera comes back.

    But the yield and the restore then had different conditions, and anything
    that emptied the list mid-gesture stranded the camera detached — with no way
    back, because every subsequent press took the same early return. Tonio: "the
    3d view seizes up so I can't rotate the view any more."

    A detached camera is a DEAD SCENE, not a cosmetic glitch, so this must not be
    conditional on anything. Whoever took the camera is responsible for giving it
    back; asking permission of the popup list is how it got lost.
    */
    if (info.type === BABYLON.PointerEventTypes.POINTERUP) {
      // With `startAndReleaseDragOnPointerEvents` off, the behaviour's own
      // release branch is gated off too — so nothing would ever end the drag
      // and the panel would stick to the cursor forever.
      if (list != null) for (const p of list) p.endDrag()
      restoreCamera()
      return
    }

    // ONLY on a press. This used to pick on every pointer event, moves
    // included — a scene pick per mouse-move, for a question that can only be
    // answered by a click. Blocking is handled by `isPickable` now, so nothing
    // here needs to run while you are merely moving the mouse.
    if (list == null || list.length === 0) return
    if (info.type !== BABYLON.PointerEventTypes.POINTERDOWN) return

    // Un-pickable panels are skipped by the pick itself, so a modal's blocking
    // needs no test here.
    /*
    ALONG THE RAY THAT PRESSED, not from the screen position. In a headset the
    press comes from a controller, and `scene.pointerX/Y` is only that
    controller projected onto the screen: picking from there casts a ray from
    the HEAD through that point, which is not where the hand is aiming. The
    title bar is a thin target, so the two disagree about it most of the time,
    and the popup could be neither dragged nor closed in VR. Flat, the event's
    ray IS the screen ray, so this is the same pick.
    */
    const isPopup = (m: BABYLON.AbstractMesh): boolean =>
      list.some((p) => p.plane.mesh === m)
    const pressRay = info.pickInfo?.ray
    /*
    A COPY AT FULL LENGTH. Babylon's XR pointer shortens its ray to the distance
    of whatever it hit, so re-casting that same ray stops a rounding error short
    of the surface it just found and reports a miss. Measured in an emulated
    session: length 1.4968695 against a hit at 1.4968695, no hit; the same
    origin and direction at full length, hit.
    */
    /*
    IN EACH POPUP'S OWN SCENE, layers first. A popup opened from a panel in
    the `ui` (or `modal`) layer is made in that layer, which is its own
    Babylon scene: picked here in the main scene it was never hit, so its ×
    and its title bar did nothing (Tonio: "I can't move it. I also can't
    close it."). A layer is drawn over the world, so it is asked first.
    */
    const ray =
      pressRay != null
        ? new BABYLON.Ray(pressRay.origin, pressRay.direction, 1e4)
        : scene.activeCamera != null
        ? scene.createPickingRay(
            scene.pointerX,
            scene.pointerY,
            BABYLON.Matrix.Identity(),
            scene.activeCamera
          )
        : null
    const scenes = [
      ...new Set(
        list
          .map((p) => p.plane.mesh?.getScene())
          .filter((s): s is BABYLON.Scene => s != null)
      ),
    ].sort((a, b) => Number(a === scene) - Number(b === scene))
    let hit: BABYLON.Nullable<BABYLON.PickingInfo> = null
    if (ray != null) {
      for (const s of scenes) {
        const h = s.pickWithRay(ray, isPopup)
        if (h?.hit) {
          hit = h
          break
        }
      }
    }
    const onPopup = hit?.hit === true ? hit.pickedMesh : null

    if (onPopup != null) {
      // CLICK to front, not just drag-to-front: raising something only when you
      // move it means a panel you merely want to READ stays buried.
      const target = list.find((p) => p.plane.mesh === onPopup)
      if (target == null) return
      bringToFront(owner, target)
      /*
      THE PRESS IS THE POPUP'S, wherever on it it landed. The settings panel
      closes on a press on the background (a DOM listener on the scene), and a
      press on a popup's × or title bar was never marked as taken, so closing
      a popup closed its parent panel with it. Its content marks its own.
      */
      claimPointer(info.event as unknown as Event)

      /*
      Where in the panel did the press land? The texture coordinate is the
      panel's own space, so the hit regions are expressed in the same units the
      chrome was drawn in — no world-space arithmetic, and it stays correct
      however the panel is rotated or scaled.

      UV v is flipped relative to SVG y (0 at the bottom), so the TITLE BAR is
      the top of the panel: v above 1 - gripHeight.
      */
      const uv = hit?.getTextureCoordinates?.()
      if (uv == null) return
      /*
      Classify in the panel's OWN units, against the same layout the chrome was
      drawn from. The first version compared a UV x (a WIDTH fraction) against
      the grip height (a HEIGHT fraction) — on a portrait panel the close region
      and the drawn × were fully disjoint, so pressing close fell through to the
      drag branch and tore the popup off its opener instead. See `popup-chrome`.
      */
      const pt = uvToViewBox(uv.x, uv.y, target.vbWidth, target.vbHeight)
      const what = chromeHit(pt.x, pt.y, target.chromeLayout)
      if (what === 'content') return // the panel's own UI must reach it
      /*
      CLAIM THE PRESS. Everything else listening for pointers (the parent
      panel behind this popup above all) would otherwise see it too: its own
      pick looks only for its own plane, so it took the press as on whatever
      lay behind the ×, and when the release came, with the popup gone, it saw
      a whole tap there. Often its own close: "Closing popups sometimes closes
      the parent." This observer is registered first, so stopping here works.
      */
      if (what === 'close') {
        // Only the close: a drag needs the rest of this event (Babylon's own
        // drag handling listens for the same press), and claiming it there
        // left the release to dismiss everything.
        if (state) state.skipNextObservers = true
        target.close()
        return
      }
      // Yield BEFORE starting: the camera must not have the press either.
      yieldCamera()
      target.beginDrag(
        (info.event as PointerEvent | undefined)?.pointerId ?? 0,
        // The point on the panel actually under the pointer, and the ray that
        // found it — see beginDrag for why both are required.
        hit?.pickedPoint ?? info.pickInfo?.pickedPoint ?? undefined,
        pressRay ?? undefined
      )
    }
  }
  // FIRST in line, so a press it claims (the title bar) reaches no one else.
  scene.onPointerObservable.add(onPointer, undefined, true)
  const layers = new WeakSet<BABYLON.Scene>()
  layerListeners.set(owner, (s: BABYLON.Scene) => {
    if (s === scene || layers.has(s)) return
    layers.add(s)
    s.onPointerObservable.add(onPointer, undefined, true)
  })
}

/**
 * How far to move `mesh` (in its parent's frame) so all of it is on screen,
 * or null when it already is or there is no flat screen to fit (a headset).
 * Pixels off an edge become world units at the mesh's depth.
 */
function intoView(mesh: BABYLON.AbstractMesh): { x: number; y: number } | null {
  const scene = mesh.getScene()
  const camera = scene.activeCamera
  if (camera == null || camera.getClassName() === 'WebXRCamera') return null
  const engine = scene.getEngine()
  const w = engine.getRenderWidth(true)
  const h = engine.getRenderHeight(true)
  if (!w || !h) return null
  mesh.refreshBoundingInfo({})
  const corners = mesh.getBoundingInfo().boundingBox.vectorsWorld
  const viewport = camera.viewport.toGlobal(w, h)
  const transform = scene.getTransformMatrix()
  let left = Infinity
  let right = -Infinity
  let top = Infinity
  let bottom = -Infinity
  for (const c of corners) {
    const s = BABYLON.Vector3.Project(
      c,
      BABYLON.Matrix.Identity(),
      transform,
      viewport
    )
    left = Math.min(left, s.x)
    right = Math.max(right, s.x)
    top = Math.min(top, s.y)
    bottom = Math.max(bottom, s.y)
  }
  const margin = 8
  let dx = 0
  let dy = 0
  // Off an edge: back in by that much. Too big to fit at all: centred.
  if (right - left < w - margin * 2) {
    if (left < margin) dx = margin - left
    else if (right > w - margin) dx = w - margin - right
  } else dx = w / 2 - (left + right) / 2
  if (bottom - top < h - margin * 2) {
    if (top < margin) dy = margin - top
    else if (bottom > h - margin) dy = h - margin - bottom
  } else dy = h / 2 - (top + bottom) / 2
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return null
  if (dx === 0 && dy === 0) return null
  const centre = mesh.getBoundingInfo().boundingBox.centerWorld
  const depth = BABYLON.Vector3.Distance(camera.globalPosition, centre)
  const fov = (camera as BABYLON.TargetCamera).fov ?? 0.8
  const perPx = (2 * depth * Math.tan(fov / 2)) / h
  // Screen right and down, as world directions.
  const world = camera
    .getDirection(BABYLON.Axis.X)
    .scale(dx * perPx)
    .add(camera.getDirection(BABYLON.Axis.Y).scale(-dy * perPx))
  const parent = mesh.parent as BABYLON.TransformNode | null
  if (parent != null) {
    const inv = parent.computeWorldMatrix(true).clone().invert()
    BABYLON.Vector3.TransformNormalToRef(world, inv, world)
  }
  return { x: world.x, y: world.y }
}

/** Per owner: hear pointer events in a layer scene a popup was made in. */
const layerListeners = new WeakMap<B3d, (s: BABYLON.Scene) => void>()

export function openPopup(owner: B3d, opts: PopupSurfaceOptions): PopupSurface {
  const {
    svg,
    opener = null,
    width = 1,
    resolution = 512,
    offset = {},
    lift = 0.02,
    draggable = true,
    modal = false,
    gripHeight = 0.2,
    chrome = true,
    handleClosed,
  } = opts

  const vb = svg.viewBox?.baseVal
  const aspect = vb && vb.width > 0 ? vb.height / vb.width : 1

  /*
  CHROME, drawn into the caller's SVG.

  The panel's content is the caller's; its HANDLES are ours, because they have
  to line up with the hit regions this module tests against. Injecting them here
  keeps the two from drifting — a move icon that isn't where the grip is would be
  worse than no icon.

  `iconGlyph` rather than `svgIcons`: this SVG is rasterized to a texture, where
  `currentColor` resolves against nothing and paints black.
  */
  const vbW = vb && vb.width > 0 ? vb.width : 100
  const vbH = vb && vb.height > 0 ? vb.height : 100
  // ONE layout, used by the drawing below AND by the hit test in `wirePointer`.
  const layout = chromeLayout(vbW, vbH, gripHeight, draggable)
  if (chrome && layout.barHeight > 0) {
    /*
    TEXTURE ONLY. These glyphs are handled by picking — uv -> viewBox ->
    `chromeHit` — which happens in the scene and nowhere else. Drawn flat they
    are decoration you can press with no effect, which is worse than absent.

    Marked rather than omitted, because the same sheet is shown in BOTH places:
    `svg-texture` keeps `texture` nodes and the DOM side hides them.
    */
    if (layout.move != null) {
      svg.appendChild(
        markPresentation(
          iconGlyph('move', {
            color: '#8fa3ba',
            size: layout.move.size,
            x: layout.move.x,
            y: layout.move.y,
          })
        )
      )
    }
    svg.appendChild(
      markPresentation(
        iconGlyph('close', {
          color: '#8fa3ba',
          size: layout.close.size,
          x: layout.close.x,
          y: layout.close.y,
        })
      )
    )
  }

  /*
  A RIM, because we cannot have a drop shadow: a popup floating in front of a
  scene has nothing for one to fall on. Tonio: "Since we can't do nice drop
  shadows around pop ups, I wonder if there's some other way we can make them
  stand out."

  The one every panel wears (`panelRim`, the corner toolbar's outline), so a
  popup and the panel it came from look like one family. A `panel3d` sheet
  already has it; anything else gets one here.
  */
  if (chrome && svg.querySelector(':scope > [data-w3d-rim]') == null) {
    svg.appendChild(panelRim(vbW, vbH, 0))
  }

  const plane = b3dSvgPlane({
    width,
    height: width * aspect,
    resolution,
    /*
    ALPHA, AS EVERY OTHER PANEL IS, but in the z-buffer.

    Popups were opaque meshes with their corners cut as geometry, because a
    plain transparent panel does not write depth: Babylon re-sorts it per
    frame by distance and near-coplanar popups swapped order as you orbited.
    That made them the one kind of panel whose outline and corners were not
    the SVG's, and the one kind a translucent theme could not see through.
    Tonio: "use the same approach for all our panels as it will also allow
    translucent themes". `'depth'` blends and still writes depth (its empty
    corners are discarded), so the ordering stays the z-buffer's. The cost is
    a discard test per pixel of a small quad.
    */
    transparent: 'depth',
    // Self-lit: a UI panel should read the same under any scene lighting.
    materialChannel: 'emissive',
    // The plane routes its own picks to the SVG's `handlePointer`; a popup with
    // no interactive content still wants this so it doesn't swallow the ray.
    pointerEvents: 'on',
    // Its opener's layer: a popup from a ui panel is drawn and picked with it
    // (and can hang from it, which a mesh in another scene could not).
    layer: panelLayerOf(opener),
  }) as B3dSvgPlane
  plane.svgElement = svg

  let tornOff = false
  let closed = false
  let quiet = false
  let drag: BABYLON.PointerDragBehavior | null = null
  // The popup's own handle, once it exists: `place` runs before or after that.
  let self: PopupSurface | null = null

  /*
  THE ELEMENT OWNS THE TRANSFORM, not the mesh.

  `AbstractMesh.render()` rewrites `mesh.position` from the element's x/y/z on
  EVERY frame, so writing `mesh.position` here is silently undone — the popups
  sat at the origin and looked unplaced. The same trap eats a drag behaviour,
  which is why the drag below syncs back into x/y/z rather than being left to
  move the mesh. (Same family as `ry` being degrees: when an element manages a
  node, the element's fields are the source of truth and the node's are output.)
  */
  let unstretch: BABYLON.Nullable<BABYLON.Observer<BABYLON.Scene>> = null
  const place = (): void => {
    const mesh = plane.mesh
    if (mesh == null) return
    /*
    The opener may be GONE by now. A popup mounts a frame or more after it is
    asked for, and a panel that is rebuilt in between disposes the plane this
    one was to hang from. Parenting to it anyway left a popup in the scene
    under a disposed node: it stayed where it was when its panel moved, and
    nothing ever closed it.
    */
    if (opener != null && opener.isDisposed()) {
      // Deferred: this can run inside `openPopup`, before `api` exists. And
      // quiet: nobody dismissed it, so nobody is told it was dismissed.
      quiet = true
      queueMicrotask(() => api.close())
      return
    }
    if (opener != null) mesh.parent = opener
    /*
    THE OPENER'S SIZE, NOT ITS STRETCH. A panel grows by scaling its plane, by
    different amounts across and down when its content changes shape, and a
    popup hung from it took the stretch too: the Tailor's outline came out half
    again too tall, off both ends of the screen. Each frame, the popup's height
    scale cancels its opener's, so it inherits a size and keeps its own shape.
    */
    unstretch ??= mesh.getScene().onBeforeRenderObservable.add(() => {
      const p = mesh.parent as BABYLON.TransformNode | null
      const sx = p?.scaling?.x ?? 1
      const sy = p?.scaling?.y ?? 1
      mesh.scaling.y = mesh.scaling.x * (sy !== 0 ? sx / sy : 1)
    })
    plane.x = offset.x ?? 0
    plane.y = offset.y ?? 0
    plane.z = (offset.z ?? 0) - lift
    // This overwrites whatever the stack had added, so the stack must not
    // believe its offset is still there (it would subtract it again and leave
    // this popup one step too far back, level with its neighbour).
    if (self != null) self.stackLift = undefined
    // Apply to the node NOW as well as to the element. The element's own render
    // writes exactly these values on its next pass, but `tearOff` reads
    // `absolutePosition` and would otherwise get a pose one frame stale — which
    // it then bakes in, dropping the panel at its opener instead of where it
    // appears.
    mesh.position.set(plane.x, plane.y, plane.z)
    mesh.computeWorldMatrix(true)
    // On a screen, keep it on the screen: shift it back into view by whatever
    // falls off an edge.
    // A few passes: in perspective one shift does not land exactly.
    for (let pass = 0; pass < 4; pass++) {
      const shift = intoView(mesh)
      if (shift == null) break
      plane.x += shift.x
      plane.y += shift.y
      mesh.position.set(plane.x, plane.y, plane.z)
      mesh.computeWorldMatrix(true)
    }
  }

  /** Run `fn` once the plane has a mesh — now, or on the first frame it does. */
  const whenMesh = (fn: () => void): void => {
    owner.whenReady(() => {
      if (plane.mesh != null) {
        fn()
        return
      }
      // The plane builds its mesh in its OWN sceneReady, which may not have run
      // when ours does. One observer beats a guessed delay.
      const obs = owner.scene.onBeforeRenderObservable.add(() => {
        if (plane.mesh == null) return
        owner.scene.onBeforeRenderObservable.remove(obs)
        fn()
      })
    })
  }

  /*
  One queue, so ORDER is guaranteed. `tearOff()` is normally called on the line
  after `openPopup()`, i.e. long before the mesh exists — with two independent
  deferrals it raced `place`, read the un-offset pose, and baked the popup onto
  its opener. Everything after mount goes through here.
  */
  let mounted = false
  const pending: Array<() => void> = []
  const afterMount = (fn: () => void): void => {
    if (mounted) fn()
    else pending.push(fn)
  }

  owner.appendChild(plane as unknown as Node)
  whenMesh(() => {
    place()
    // Take a place in the stack now. Without this, two popups opened one
    // after the other sat at the same depth until one of them was clicked.
    // Deferred: on a live scene this runs before `api` exists.
    queueMicrotask(() => {
      if (!closed) bringToFront(owner, api)
    })
    // Owned popups are draggable too, and stay owned. Without this, popup 0
    // was simply immovable with nothing saying why, which is a rule you have
    // to be TOLD rather than one you can discover.
    attachDrag()
    wirePointer(owner)
    const ms = plane.mesh?.getScene()
    if (ms != null) layerListeners.get(owner)?.(ms)
    applyModalBlocking(owner)
    mounted = true
    for (const fn of pending.splice(0)) fn()
  })

  /**
   * Attach the move gesture. Shared, because an owned popup gets one too: it
   * moves relative to its opener and stays owned.
   */
  function attachDrag(): void {
    /*
    A FUNCTION DECLARATION, DELIBERATELY — it has to HOIST.

    This is called from the `whenMesh` block ABOVE, and as a `const` arrow it
    sat in its temporal dead zone whenever that block ran synchronously. Which
    is exactly the case that matters: `whenReady` fires immediately when the
    scene is already up, and appending the plane to a live scene runs its
    `sceneReady` synchronously too, so `plane.mesh` is non-null on the spot and
    the whole chain completes inside `openPopup`.

    So a popup opened at MOUNT worked (scene not ready, callback deferred, const
    initialised by the time it ran) and a popup opened from a CLICK threw
    `ReferenceError: Cannot access 'attachDrag' before initialization` — which,
    minified, reads "Cannot access 'E' before initialization" and names nothing
    you can search for.

    The throw happened before `openPopup` returned, so the caller got no popup
    and no drag, and any code after the call never ran. That is the whole of the
    reverted demo's "the panel doesn't appear in the DOM" and "I can't drag it":
    one exception, two symptoms, neither pointing here.
    */
    const mesh = plane.mesh
    if (mesh == null || drag != null || !draggable) return
    /*
    DRAG IN THE PANEL'S OWN PLANE.

    A free drag is fine looking straight at the panel and comes apart the moment
    you orbit: the drag plane stops relating to the panel, so a small hand
    movement throws it across the room. Tonio: "if you rotate the camera and
    drag a surface, horrible things happen." Constraining to the panel's own
    normal makes the gesture mean the same thing from every angle.
    */
    drag = new BABYLON.PointerDragBehavior({
      dragPlaneNormal: new BABYLON.Vector3(0, 0, 1),
    })
    drag.useObjectOrientationForDragging = true
    // WE decide when a drag starts — the scene hook does the hit test against
    // the title bar first. Left to itself the behaviour grabs on any press
    // anywhere on the panel, which would make every button inside it a
    // failed drag.
    if (gripHeight > 0) drag.startAndReleaseDragOnPointerEvents = false
    /*
    A HEADSET DRAG DIED ON ITS FIRST FRAME without this. The behaviour cancels
    a drag when "another button" arrives on the same pointer, meaning any event
    whose `button` is neither -1 nor the button that started it. `startDrag()`
    records no starting button (-1), a mouse move carries -1 so it passes, and
    Babylon's XR pointer synthesises its moves as `new PointerEvent('pointermove')`,
    whose button defaults to 0. So in VR the first move after the press
    released the drag: the popup tore off and then sat there. We end the drag
    ourselves on POINTERUP, so the rule has nothing to protect.
    */
    drag.allowOtherButtonsDuringDrag = true
    mesh.addBehavior(drag)
    drag.onDragStartObservable.add(() => {
      /*
      AN OWNED POPUP STAYS OWNED WHEN YOU MOVE IT.

      Dragging used to tear it off ("pull a tab out of the window"). In a
      headset that read as a bug: move the Perf Stats popup, then move the
      panel it came from, and the popup stayed where it was (Tonio: "it loses
      its parent ... and never gets reattached"). You moved it to see past it,
      not to disown it. The behaviour drags by absolute position, so a parented
      mesh needs nothing special: its local position is what changes, and that
      is what the element keeps. `tearOff()` is still there for a caller that
      means it.
      */
      bringToFront(owner, api)
    })
    // The behaviour moves the MESH; the element would overwrite that next
    // frame. Copy back so the two agree and the panel stays where dropped.
    drag.onDragObservable.add(() => {
      plane.x = mesh.position.x
      plane.y = mesh.position.y
      plane.z = mesh.position.z
    })
  }

  const api: PopupSurface = {
    plane,
    modal,
    vbWidth: vbW,
    vbHeight: vbH,
    chromeLayout: layout,
    beginDrag(
      pointerId: number,
      pickedPoint?: BABYLON.Vector3,
      ray?: BABYLON.Ray
    ) {
      if (drag == null || !draggable) return
      /*
      AFTER THE WHOLE EVENT, not merely after our own handler.

      `PointerDragBehavior`'s POINTERDOWN handler releases any drag whose
      `_activeDragButton` doesn't match the event's button, and `startDrag()`
      leaves that at -1 — so whichever observer runs LAST wins. Registration
      order decides that, and it is not stable here: `wirePointer` installs once
      on the first popup's mount, so popup 0's behaviour registers before it and
      every later popup's registers after. The first popup dragged; the rest did
      not. That is what Tonio hit — "I cannot move the pop-ups, none of them" —
      after I had already "fixed" this once by reordering.

      A microtask runs after every observer for this event, so the answer stops
      depending on who registered first. Ordering was the wrong lever; the right
      one is to leave the event entirely.
      */
      const id = pointerId
      /*
      PASS THE GRAB POINT, or the panel jumps.

      `startDrag(id)` alone leaves Babylon with no start point, so it grabs at
      the MESH ORIGIN — the panel snaps until its centre is under the pointer
      and then follows. Tonio: "when you click on an element it drifts to
      centre the mouse pointer rather than preserving the offset." Handing it
      the point actually under the pointer makes the panel stay where you took
      hold of it, which is what every drag anywhere else does.

      Cloned because the pick info is reused by the scene: keeping the live
      vector means the grab point quietly becomes wherever the pointer is now,
      which is the same bug wearing a disguise.
      */
      const grab = pickedPoint?.clone()
      /*
      THE RAY MATTERS AS MUCH AS THE POINT.

      Passing only `startPickedPoint` was not enough — Babylon's `_startDrag`
      falls back to a ray built from `activeCamera.position` when `fromRay` is
      omitted, so it still had no idea where the POINTER was and recomputed the
      drag plane from that default. The panel kept snapping to centre. Tonio,
      after the first attempt: "the popup-surface is still drifting to center."

      Both are needed: the ray says where the gesture is pointing, the point
      says where on the panel it took hold. Cloned for the same reason as the
      point — Babylon reuses its pick info between events.
      */
      const grabRay =
        ray != null
          ? new BABYLON.Ray(
              ray.origin.clone(),
              ray.direction.clone(),
              // NOT `ray.length`: an XR pointer's ray is cut to its hit
              // distance, and Babylon starts the drag by casting this ray at
              // a plane through the grab point. A ray that ends exactly there
              // misses it, and the drag silently never starts.
              1e4
            )
          : undefined
      queueMicrotask(() => {
        if (drag == null || closed) return
        drag.startDrag(id, grabRay, grab)
      })
    },
    endDrag() {
      if (drag?.dragging === true) drag.releaseDrag()
    },
    toFront() {
      bringToFront(owner, api)
    },
    get tornOff() {
      return tornOff
    },
    tearOff() {
      if (tornOff || closed) return
      tornOff = true
      // Queued BEHIND placement, not merely deferred — see the note above.
      afterMount(() => {
        const mesh = plane.mesh
        if (mesh == null || closed) return
        /*
        `setParent(null)`, not `parent = null`: the first PRESERVES world pose
        by baking the parent's transform into the child; the second drops it and
        the panel teleports to wherever its local offset points. Not moving at
        the instant it stops being owned is the whole of "tearing off".
        */
        const world = mesh.absolutePosition.clone()
        mesh.setParent(null)
        // The stack's offset was in the old parent's space and is now part
        // of the world pose; it must not be subtracted as if it were not.
        api.stackLift = undefined
        // …and hand the world pose to the ELEMENT, or its per-frame sync writes
        // the old parent-relative offset straight back over it.
        plane.x = world.x
        plane.y = world.y
        plane.z = world.z
        /*
        …AND THE ORIENTATION, which the element also rewrites every frame.

        Position alone was enough only while every opener happened to be
        upright. The headset's settings panel is tilted back to face you, so a
        popup torn off it snapped vertical: it turned edge-on to the viewer, and
        because the drag plane is the panel's own plane, a controller ray aimed
        up at it met that plane metres away. Measured in an emulated session: a
        0.25 m sweep of the pointer threw the popup 7 m into the sky.
        `setParent(null)` has just baked the world rotation into the mesh, so
        read it back in the element's own yaw/pitch/roll.
        */
        const e = mesh.rotationQuaternion?.toEulerAngles()
        if (e != null) {
          const deg = 180 / Math.PI
          plane.rx = e.x * deg
          plane.ry = e.y * deg
          plane.rz = e.z * deg
        }
        attachDrag()
      })
    },
    close() {
      if (closed) return
      closed = true
      // Remove first, THEN re-evaluate: if this was the modal, whatever it was
      // blocking has to become reachable again.
      if (drag != null) plane.mesh?.removeBehavior(drag)
      unstretch?.remove()
      unstretch = null
      const list = openPopups.get(owner)
      if (list != null) {
        const i = list.indexOf(api)
        if (i >= 0) list.splice(i, 1)
      }
      plane.remove()
      applyModalBlocking(owner)
      // LAST, and after `closed` is set: a handler that closes its siblings can
      // reach back here, and must find this one already gone rather than recurse.
      if (!quiet) handleClosed?.()
    },
  }

  self = api
  // Newest on top, which is what "it just opened" should mean.
  const list = openPopups.get(owner) ?? []
  list.unshift(api)
  openPopups.set(owner, list)

  /*
  RE-APPLY, because the mount above may already have run.

  `owner.appendChild` drains the ready queue SYNCHRONOUSLY once the scene is up,
  so for a popup opened at runtime `applyModalBlocking` fires during mount —
  before this registration — and reads a list that does not contain this popup.
  A `modal: true` popup therefore found no modal, set every OTHER popup back to
  `isPickable = true`, and blocked nothing at all.

  It only looked correct in the doc demo, which opens inside `sceneCreated`
  while the queue is still deferred, so registration wins the race there.

  Running it here as well is the fix and is idempotent: the mount-time call
  still covers the deferred path (where `plane.mesh` may not exist yet, which
  `applyModalBlocking` already skips).
  */
  applyModalBlocking(owner)
  bringToFront(owner, api)

  return api
}
